import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  FitnessCenter,
} from '@mui/icons-material';
import { WorkoutSession, WorkoutDay, Exercise } from '../types/workout';
import { WorkoutUtils } from '../utils/workoutUtils';
import { HeaderBar } from './HeaderBar';
import { useWorkout } from '../hooks/useWorkout';
import exerciseData from '../exercises.json';

// Format set label based on type and number (W1, W2, S1, S2)
const formatSetLabel = (setType: string, setNumber: number) => {
  const prefix = setType === 'warmup' ? 'W' : 'S';
  return `${prefix}${setNumber}`;
};

// Component to display expected exercises for a skipped day
const SkippedWorkoutDisplay: React.FC<{ week: number; day: WorkoutDay }> = ({ week, day }) => {
  // Get expected exercises for this week and day
  const program = exerciseData as any;
  const weekData = program[week.toString()];
  const expectedExercises: Exercise[] = weekData && Array.isArray(weekData[day]) ? weekData[day] : [];

  if (expectedExercises.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No exercises scheduled for this day
      </Typography>
    );
  }

  const generateSkippedSets = (exercise: Exercise) => {
    const sets = [];
    
    // Parse warmup sets
    const warmupRange = WorkoutUtils.parseSetRange(exercise.warmup_sets);
    for (let i = 0; i < warmupRange.max; i++) {
      sets.push({
        setNumber: i + 1,
        setType: 'warmup' as const,
        weight: 0,
        reps: 0
      });
    }
    
    // Parse working sets
    const workingRange = WorkoutUtils.parseSetRange(exercise.working_sets);
    for (let i = 0; i < workingRange.max; i++) {
      sets.push({
        setNumber: i + 1,
        setType: 'working' as const,
        weight: 0,
        reps: 0
      });
    }
    
    return sets;
  };

  return (
    <Box>
      <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic', mb: 2 }}>
        This workout was skipped. Below are the exercises that were scheduled:
      </Typography>
      
      {expectedExercises.map((exercise, exerciseIndex) => {
        const skippedSets = generateSkippedSets(exercise);
        
        return (
          <Box key={exerciseIndex} sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {exercise.exercise}
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ opacity: 0.7 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Set</strong></TableCell>
                    <TableCell align="center"><strong>Weight</strong></TableCell>
                    <TableCell align="center"><strong>Reps</strong></TableCell>
                    <TableCell align="center"><strong>Volume</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skippedSets.map((set, setIndex) => (
                    <TableRow key={setIndex}>
                      <TableCell>{formatSetLabel(set.setType, set.setNumber)}</TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          Skipped
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total Volume: 0 lbs (Skipped)
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export const WeeklyDetailPage: React.FC = () => {
  const { weekNumber } = useParams<{ weekNumber: string }>();
  const { userProgress } = useWorkout();
  
  const weekNum = weekNumber ? parseInt(weekNumber) : 0;
  const weekSessions = WorkoutUtils.getWeekSessions(userProgress, weekNum);
  
  // Group sessions by day - if there are multiple sessions for the same day, keep the most complete one
  const sessionsByDay = weekSessions.reduce((acc, session) => {
    const existingSession = acc[session.day];
    
    if (!existingSession) {
      // No existing session for this day, add it
      acc[session.day] = session;
    } else {
      // There's already a session for this day, keep the more complete one
      const existingExerciseCount = existingSession.exercises.length;
      const currentExerciseCount = session.exercises.length;
      const existingCompletedSets = existingSession.exercises.reduce((total, ex) => 
        total + ex.sets.filter(set => set.completed).length, 0);
      const currentCompletedSets = session.exercises.reduce((total, ex) => 
        total + ex.sets.filter(set => set.completed).length, 0);
      
      // Prefer the session with more exercises, or if equal, more completed sets
      if (currentExerciseCount > existingExerciseCount || 
          (currentExerciseCount === existingExerciseCount && currentCompletedSets > existingCompletedSets)) {
        acc[session.day] = session;
        console.log(`🔄 Replaced session for ${session.day} with more complete one:`, {
          old: { exercises: existingExerciseCount, completedSets: existingCompletedSets },
          new: { exercises: currentExerciseCount, completedSets: currentCompletedSets }
        });
      }
    }
    
    return acc;
  }, {} as Record<WorkoutDay, WorkoutSession>);

  // Get all workout days in order
  const workoutDays: WorkoutDay[] = [
    WorkoutDay.MONDAY, 
    WorkoutDay.TUESDAY, 
    WorkoutDay.THURSDAY, 
    WorkoutDay.FRIDAY, 
    WorkoutDay.SATURDAY
  ];
  
  // Check if it's a ramp up week
  const isRampUpWeek = [1, 2, 6, 7].includes(weekNum);

  // Common accordion styles (same pattern as ExerciseInfo)
  const accordionStyles = {
    borderRadius: '24px !important', 
    '&:before': { display: 'none' },
    '&.Mui-expanded': { borderRadius: '24px !important' },
    '& .MuiAccordionSummary-root': { 
      borderRadius: '24px !important',
      '&.Mui-expanded': { borderRadius: '24px 24px 0 0 !important' }
    },
    '& .MuiAccordionDetails-root': { 
      borderRadius: '0 0 24px 24px !important' 
    },
    overflow: 'hidden'
  };

  const getDayTypeAndSubtitle = (day: WorkoutDay) => {
    const workoutType = WorkoutUtils.getWorkoutType(day);
    return workoutType;
  };

  const formatWeight = (set: any) => {
    if (!set.completed) {
      return set.weight === 0 ? '-' : `${set.weight} lbs`;
    }
    return set.weight === 0 ? 'Skipped' : `${set.weight} lbs`;
  };

  const formatReps = (set: any) => {
    if (!set.completed) {
      return set.reps === 0 ? '-' : set.reps.toString();
    }
    return set.reps === 0 ? '-' : set.reps.toString();
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderBar 
        title={`Week ${weekNum} Details`} 
        showBackButton={true}
      />
      <Container maxWidth="md" sx={{ mt: 'calc(56px + env(safe-area-inset-top, 0px) + 24px)', pb: 4 }}>
        {/* Week Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Week {weekNum} Details
            </Typography>
            {isRampUpWeek && (
              <Chip 
                label="Ramp Up Week" 
                size="small" 
                sx={{ 
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText'
                }} 
              />
            )}
          </Box>
          <Typography variant="h6" color="text.secondary">
            Detailed workout history and performance
          </Typography>
        </Box>

        {/* Workout Days */}
        <Box sx={{ mt: 3 }}>
          {workoutDays.map((day) => {
            const session = sessionsByDay[day];
            const dayType = getDayTypeAndSubtitle(day);
            
            // Determine if this is a skipped workout vs not started
            const isSkipped = session && session.notes && session.notes.trim().toLowerCase() === 'skipped';
            const isInProgress = session && !session.completed && !isSkipped;
            const isCompleted = session && session.completed;
            const isNotStarted = !session;
            
            return (
              <Accordion 
                key={day}
                sx={{ 
                  ...accordionStyles,
                  mb: 2
                }}
                defaultExpanded={!!session}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <FitnessCenter 
                      sx={{ 
                        color: isCompleted 
                          ? 'success.dark' 
                          : isInProgress 
                            ? 'warning.dark'
                            : isSkipped
                              ? 'error.dark'
                              : 'text.secondary'
                      }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {day}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dayType}
                      </Typography>
                    </Box>
                    <Chip 
                      label={
                        isCompleted 
                          ? 'Completed'
                          : isInProgress 
                            ? 'In Progress'
                            : isSkipped
                              ? 'Skipped'
                              : 'Not Started'
                      } 
                      size="small"
                      color={
                        isCompleted 
                          ? 'success'
                          : isInProgress 
                            ? 'warning'
                            : isSkipped
                              ? 'error'
                              : 'default'
                      }
                      variant={session ? 'filled' : 'outlined'}
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {session ? (
                    <Box>
                      {isSkipped ? (
                        <SkippedWorkoutDisplay week={weekNum} day={day} />
                      ) : (
                        session.exercises.map((exercise, exerciseIndex) => (
                          <Box key={exerciseIndex} sx={{ mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {exercise.exerciseName}
                            </Typography>
                            
                            {exercise.sets.length > 0 ? (
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Set</strong></TableCell>
                                      <TableCell align="center"><strong>Weight</strong></TableCell>
                                      <TableCell align="center"><strong>Reps</strong></TableCell>
                                      <TableCell align="center"><strong>Volume</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {exercise.sets.map((set, setIndex) => (
                                      <TableRow key={setIndex}>
                                        <TableCell>{formatSetLabel(set.setType, set.setNumber)}</TableCell>
                                        <TableCell align="center">
                                          {formatWeight(set)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {formatReps(set)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {!set.completed && set.weight === 0 && set.reps === 0 
                                            ? '-' 
                                            : set.weight === 0 
                                              ? '-' 
                                              : `${set.weight * set.reps} lbs`
                                          }
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No sets recorded for this exercise
                              </Typography>
                            )}
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Total Volume: {exercise.totalVolume} lbs
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Workout Not Started
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This workout hasn't been started yet.
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}; 